import { useEffect, useState, type FormEvent } from 'react';
import { api, ApiError } from '../../lib/api';
import { BandChip, ErrorNote, RiskTierChip, SectionTitle, Spinner } from '../../components/ui';

interface UseCase {
  id: string;
  name: string;
  description: string | null;
  business_owner: string | null;
  value_score: number;
  feasibility_score: number;
  readiness_score: number;
  risk_score: number;
  time_to_value_score: number;
  priority_score: number | null;
  priority_band: string | null;
  risk_tier: string | null;
  status: string;
  recommended_action: string | null;
  go_no_go: string | null;
  go_no_go_reason: string | null;
  go_no_go_by: string | null;
}

const AXES = [
  ['value_score', 'Value'],
  ['feasibility_score', 'Feasibility'],
  ['readiness_score', 'Readiness'],
  ['risk_score', 'Risk'],
  ['time_to_value_score', 'Speed'],
] as const;

const STATUS_OPTIONS = ['candidate', 'pilot', 'roadmap', 'deferred', 'rejected'];

const EMPTY_FORM = {
  name: '', description: '', business_owner: '',
  value_score: 3, feasibility_score: 3, readiness_score: 3, risk_score: 3, time_to_value_score: 3,
};

export function UseCasesTab({ engagementId, reload }: { engagementId: string; reload: () => Promise<void> }) {
  const [items, setItems] = useState<UseCase[] | null>(null);
  const [form, setForm] = useState({ ...EMPTY_FORM });
  const [showForm, setShowForm] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [decisionFor, setDecisionFor] = useState<UseCase | null>(null);
  const [decisionReason, setDecisionReason] = useState('');

  async function load() {
    try {
      const r = await api.get<{ useCases: UseCase[] }>(`/api/engagements/${engagementId}/use-cases`);
      setItems(r.useCases);
    } catch {
      setError('Failed to load use cases.');
    }
  }
  useEffect(() => { void load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [engagementId]);

  async function create(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError('');
    try {
      await api.post(`/api/engagements/${engagementId}/use-cases`, form);
      setForm({ ...EMPTY_FORM });
      setShowForm(false);
      await load();
      await reload();
    } catch {
      setError('Failed to add use case.');
    } finally {
      setBusy(false);
    }
  }

  async function patchScores(uc: UseCase, field: string, value: number) {
    try {
      await api.patch(`/api/use-cases/${uc.id}`, { [field]: value });
      await load();
    } catch {
      setError('Update failed.');
    }
  }

  async function setStatus(uc: UseCase, status: string) {
    setError('');
    try {
      await api.patch(`/api/use-cases/${uc.id}`, { status });
      await load();
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        setDecisionFor(uc);
      } else {
        setError('Status change failed.');
      }
    }
  }

  async function decide(decision: 'go' | 'no_go') {
    if (!decisionFor) return;
    setBusy(true);
    try {
      await api.post(`/api/use-cases/${decisionFor.id}/decision`, { decision, reason: decisionReason });
      setDecisionFor(null);
      setDecisionReason('');
      await load();
    } catch {
      setError('Decision failed — a written reason (10+ chars) is required.');
    } finally {
      setBusy(false);
    }
  }

  async function remove(uc: UseCase) {
    if (!confirm(`Delete use case "${uc.name}"? Its NIST mappings are removed too.`)) return;
    await api.del(`/api/use-cases/${uc.id}`);
    await load();
    await reload();
  }

  if (!items) return <Spinner />;

  return (
    <div>
      <SectionTitle
        right={<button className="btn-primary !px-3 !py-1.5 text-xs" onClick={() => setShowForm((v) => !v)}>+ Add use case</button>}
      >
        Use-case portfolio
      </SectionTitle>
      {error && <ErrorNote message={error} />}

      {showForm && (
        <form onSubmit={create} className="card mb-4 space-y-3">
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <label className="label">Name</label>
              <input className="input" required minLength={3} value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} placeholder="e.g. AI triage of support tickets" />
            </div>
            <div>
              <label className="label">Business owner</label>
              <input className="input" value={form.business_owner} onChange={(e) => setForm((f) => ({ ...f, business_owner: e.target.value }))} placeholder="Who owns the outcome?" />
            </div>
          </div>
          <div>
            <label className="label">Description</label>
            <textarea className="input min-h-16" value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} />
          </div>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-5">
            {AXES.map(([key, label]) => (
              <div key={key}>
                <label className="label">{label} {key === 'risk_score' ? '(5=worst)' : ''}</label>
                <select className="input" value={form[key]} onChange={(e) => setForm((f) => ({ ...f, [key]: Number(e.target.value) }))}>
                  {[0, 1, 2, 3, 4, 5].map((n) => <option key={n} value={n}>{n}</option>)}
                </select>
              </div>
            ))}
          </div>
          <button className="btn-primary" disabled={busy}>{busy ? 'Adding…' : 'Add & score'}</button>
        </form>
      )}

      {decisionFor && (
        <div className="card mb-4 border-amber-700/60">
          <h3 className="font-bold text-amber-300">Go / No-Go decision required — {decisionFor.name}</h3>
          <p className="mt-1 text-xs text-slate-400">
            This use case is high-risk (risk {decisionFor.risk_score}/5). Document the decision before promoting it (NIST MANAGE 1.1 / PRD Module G).
          </p>
          <textarea
            className="input mt-3 min-h-20"
            placeholder="Decision rationale — risks considered, controls required, conditions…"
            value={decisionReason}
            onChange={(e) => setDecisionReason(e.target.value)}
          />
          <div className="mt-3 flex gap-2">
            <button className="btn-primary !py-1.5 text-xs" disabled={busy || decisionReason.trim().length < 10} onClick={() => void decide('go')}>✓ GO — approve</button>
            <button className="btn-danger !py-1.5 text-xs" disabled={busy || decisionReason.trim().length < 10} onClick={() => void decide('no_go')}>✗ NO-GO — defer</button>
            <button className="btn-secondary !py-1.5 text-xs" onClick={() => setDecisionFor(null)}>Cancel</button>
          </div>
        </div>
      )}

      {items.length === 0 ? (
        <div className="card py-12 text-center text-sm text-slate-500">
          No use cases yet. Capture candidates from discovery, score them on five axes, and the
          portfolio bands (Quick Win / Strategic Bet / Governance First) compute automatically.
        </div>
      ) : (
        <div className="space-y-3">
          {items.map((uc) => (
            <div key={uc.id} className="card">
              <div className="flex flex-wrap items-start gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <h3 className="font-bold text-slate-100">{uc.name}</h3>
                    <BandChip band={uc.priority_band} />
                    <RiskTierChip tier={uc.risk_tier} decision={uc.go_no_go} />
                    <span className="chip border-slate-700 text-slate-400">{uc.status}</span>
                  </div>
                  {uc.description && <p className="mt-1 text-sm text-slate-400">{uc.description}</p>}
                  {uc.business_owner && <p className="mt-0.5 text-xs text-slate-500">Owner: {uc.business_owner}</p>}
                  {uc.recommended_action && <p className="mt-1 text-xs text-brand-300">→ {uc.recommended_action}</p>}
                  {uc.go_no_go && (
                    <p className="mt-1 text-xs text-slate-500">
                      Decision: <strong className={uc.go_no_go === 'go' ? 'text-green-400' : 'text-red-400'}>{uc.go_no_go.toUpperCase()}</strong> by {uc.go_no_go_by} — {uc.go_no_go_reason}
                    </p>
                  )}
                </div>
                <div className="flex flex-col items-end gap-2">
                  <div className="text-right">
                    <div className="text-2xl font-bold text-slate-50">{uc.priority_score ?? '—'}</div>
                    <div className="text-[10px] uppercase tracking-wide text-slate-500">Priority</div>
                  </div>
                  <select className="input !w-32 !py-1 text-xs" value={uc.status} onChange={(e) => void setStatus(uc, e.target.value)}>
                    {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
              </div>
              <div className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-5">
                {AXES.map(([key, label]) => (
                  <div key={key} className="flex items-center gap-2">
                    <span className="w-20 text-xs text-slate-500">{label}</span>
                    <select className="input !w-16 !px-2 !py-1 text-xs" value={uc[key]} onChange={(e) => void patchScores(uc, key, Number(e.target.value))}>
                      {[0, 1, 2, 3, 4, 5].map((n) => <option key={n} value={n}>{n}</option>)}
                    </select>
                  </div>
                ))}
              </div>
              <div className="mt-2 flex justify-end gap-2">
                {uc.risk_tier === 'high' && !uc.go_no_go && (
                  <button className="btn-secondary !px-2.5 !py-1 text-xs" onClick={() => { setDecisionFor(uc); setDecisionReason(''); }}>
                    ⚖️ Record go/no-go
                  </button>
                )}
                <button className="btn-danger !px-2.5 !py-1 text-xs" onClick={() => void remove(uc)}>Delete</button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
